import { FC, useContext, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Alert,
  AlertTitle,
  Paper,
  Divider,
} from "@mui/material";
import { FloatingCard } from "src/components/molecules/assessment/FloatingCard";
import { NioshDto } from "src/app/services/generatedApi";
import { AssessmentContext } from "src/context/AssessmentContext";
import { calculateNioshAssessment } from "src/app/logic/assessment/niosh/calculate";
import { JobInfoSection } from "./JobInfoSection";
import { NioshAssessmentTable } from "./NioshAssessmentTable";
import { MeasurementSystemToggle } from "src/components/atoms/MeasurementSystemToggle";
import {
  getRiskDescription,
  getRiskColor,
} from "src/app/logic/assessment/niosh/calculate";
import {
  MeasurementSystem,
  convertNioshData,
  getUnits,
} from "src/utils/measurementConversions";

type NIOSHPageProps = {
  fileName?: string;
  havePose: boolean;
};

const defaultNioshData: NioshDto = {
  jobInfo: {
    date: undefined,
    company: undefined,
    department: undefined,
    task: undefined,
    supervisor: undefined,
    evaluator: undefined,
  },
  origin: {
    loadWeightLb: 23,
    horizontalDistanceIn: 12,
    verticalHeightIn: 30,
    asymmetryAngleDeg: 0,
    liftFrequencyPerMin: 1,
    durationCategory: 2,
    coupling: "GOOD",
  },
  destination: {
    loadWeightLb: 23,
    horizontalDistanceIn: 12,
    verticalHeightIn: 40,
    asymmetryAngleDeg: 0,
    liftFrequencyPerMin: 1,
    durationCategory: 2,
    coupling: "GOOD",
  },
};

export const NIOSHPage: FC<NIOSHPageProps> = ({ fileName, havePose }) => {
  const { assessmentDataDto, setAssessmentDataDto } =
    useContext(AssessmentContext);

  const [measurementSystem, setMeasurementSystem] =
    useState<MeasurementSystem>("US");

  const nioshData = assessmentDataDto?.niosh || defaultNioshData;
  const units = getUnits(measurementSystem);

  // Convert data for calculations (always done in US units)
  const calculationData = useMemo(() => {
    if (measurementSystem === "US") {
      return nioshData;
    }
    // Convert from metric to US for calculations
    return convertNioshData(nioshData, "METRIC", "US");
  }, [nioshData, measurementSystem]);

  const results = useMemo(() => {
    if (!calculationData) return undefined;
    return calculateNioshAssessment(calculationData);
  }, [calculationData]);

  const updateNiosh = (newNiosh: NioshDto) => {
    setAssessmentDataDto({ niosh: newNiosh });
  };

  const handleMeasurementSystemChange = (newSystem: MeasurementSystem) => {
    if (newSystem !== measurementSystem) {
      // Convert the existing data to the new measurement system
      const convertedData = convertNioshData(
        nioshData,
        measurementSystem,
        newSystem,
      );
      updateNiosh(convertedData);
      setMeasurementSystem(newSystem);
    }
  };

  const handleJobInfoUpdate = (jobInfo: typeof nioshData.jobInfo) => {
    updateNiosh({ ...nioshData, jobInfo });
  };

  const handleOriginUpdate = (origin: typeof nioshData.origin) => {
    updateNiosh({ ...nioshData, origin });
  };

  const handleDestinationUpdate = (
    destination: typeof nioshData.destination,
  ) => {
    updateNiosh({ ...nioshData, destination });
  };

  const getRiskSeverity = (riskLevel: string) => {
    switch (riskLevel) {
      case "ACCEPTABLE":
        return "success" as const;
      case "MODERATE_RISK":
        return "warning" as const;
      case "HIGH_RISK":
        return "error" as const;
      default:
        return "info" as const;
    }
  };

  const formatRiskLevel = (riskLevel: string) => {
    switch (riskLevel) {
      case "ACCEPTABLE":
        return "Acceptable Risk";
      case "MODERATE_RISK":
        return "Moderate Risk";
      case "HIGH_RISK":
        return "High Risk";
      default:
        return riskLevel;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {fileName && <FloatingCard fileName={fileName} havePose={havePose} />}

      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "grey.50" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              NIOSH Lifting Assessment
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The Revised NIOSH Lifting Equation (RNLE) calculates a Recommended
              Weight Limit (RWL) and Lifting Index (LI) to assess the risk of
              lifting-related injuries.
            </Typography>
          </Box>
          <Box sx={{ ml: 3, mt: 1 }}>
            <MeasurementSystemToggle
              system={measurementSystem}
              onChange={handleMeasurementSystemChange}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {measurementSystem === "METRIC" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> NIOSH calculations are performed using the
              original US customary units. Your metric inputs are automatically
              converted for accurate assessment.
            </Typography>
          </Alert>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <JobInfoSection
            jobInfo={nioshData.jobInfo}
            onUpdate={handleJobInfoUpdate}
          />
        </Grid>

        {results && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 3,
                bgcolor: getRiskColor(results.riskLevel) + "10",
                borderRadius: 2,
                border: `2px solid ${getRiskColor(results.riskLevel)}40`,
                mb: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="h6" fontWeight="bold">
                  Overall Assessment Results
                </Typography>
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: getRiskColor(results.riskLevel),
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  {formatRiskLevel(results.riskLevel)}
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Overall Lifting Index:</strong>{" "}
                    {results.overallLiftingIndex}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getRiskDescription(results.riskLevel)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Origin LI:</strong> {results.origin.liftingIndex} |
                    <strong> Destination LI:</strong>{" "}
                    {results.destination.liftingIndex}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The higher of the two lifting indices determines the overall
                    risk level. (RWL values shown in {units.weight})
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <NioshAssessmentTable
            originData={nioshData.origin}
            destinationData={nioshData.destination}
            onOriginUpdate={handleOriginUpdate}
            onDestinationUpdate={handleDestinationUpdate}
            measurementSystem={measurementSystem}
            results={results}
          />
        </Grid>

        {results && results.riskLevel !== "ACCEPTABLE" && (
          <Grid item xs={12}>
            <Alert severity={getRiskSeverity(results.riskLevel)}>
              <AlertTitle>Recommendations</AlertTitle>
              {results.riskLevel === "MODERATE_RISK" &&
                "Consider implementing engineering controls to reduce lifting demands. Review task design and consider mechanical aids such as lift tables, conveyors, or reducing the weight of the load."}
              {results.riskLevel === "HIGH_RISK" &&
                "Immediate redesign of the lifting task is strongly recommended. This task exceeds safe lifting capabilities for most workers. Consider automation, mechanical lifting aids, or complete task redesign."}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};
